import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  addMessages,
  IMessageView,
  requireMessagesBeforeMessage,
} from '../messages/messagesSlice';
import { RootState, TThunkApi } from 'store/store';
import * as messApi from 'api/MessApi';
import * as manApi from 'api/ManApi';
import { NumberOrNull, StringOrNull } from 'store/entity/types';

export interface IChats {
  id: number;
  muted: boolean;
  group: boolean;
  topic: string;
  messages: Array<IMessageView>;
  readToken: string;
  properties: string;
  initiatorId: number;
  creationDate: number;
  participantIds: number[];
  lastMessageDate: number;
  lastMessageSeen: { [userId: number]: number };
  sendMessageToken: string;
  myLastMessageSeen: number;
  notifyOnNewMessage: boolean;
}
export interface IConversation extends IChats {
  oldestMessage: {
    id: number;
    sendTime: number;
  };
  newestMessage: {
    id: number;
    sendTime: number;
  };
  messages: Array<IMessageView>;
  loadingOlderMessages?: Promise<any>;
  loadOlderMessagesError: any;
  loadAllOlderMessages: boolean;
}

export interface IConversationState {
  data: { [key: number]: IConversation };
  gotAllConversations: boolean;
  systemId: number;
}

const initialState: IConversationState = {
  data: {},
  gotAllConversations: false,
  systemId: 8,
};

// Helpers
function manipulateConversation(
  conversation: IConversation,
  oldConversation?: IConversation
) {
  const newConversation = { ...conversation };
  newConversation.properties = newConversation.properties ?? {};

  let oldestMessageId = Infinity;
  let oldestMessageTime = Infinity;
  let newestMessageId = 0;
  let newestMessageTime = 0;
  let messages: IConversation['messages'] | undefined = undefined;

  if (oldConversation !== undefined) {
    oldestMessageId = oldConversation.oldestMessage.id;
    oldestMessageTime = oldConversation.oldestMessage.sendTime;
    newestMessageId = oldConversation.oldestMessage.id;
    newestMessageTime = oldConversation.oldestMessage.sendTime;

    messages = oldConversation.messages;
    newConversation.loadingOlderMessages = oldConversation.loadingOlderMessages;
    newConversation.loadOlderMessagesError =
      oldConversation.loadOlderMessagesError;
    newConversation.loadAllOlderMessages = oldConversation.loadAllOlderMessages;
  }
  if (oldestMessageTime === undefined) {
    oldestMessageTime = Infinity;
  }
  if (newestMessageTime === undefined) {
    newestMessageTime = 0;
  }
  if (messages === undefined) {
    messages = [];
  }
  newConversation.messages = newConversation.messages.reduce((accu, curr) => {
    const manipulated = { ...curr };
    accu.push(manipulated);
    if (manipulated.sendTime < oldestMessageTime) {
      oldestMessageTime = manipulated.sendTime;
      oldestMessageId = manipulated.id;
    }
    if (manipulated.sendTime > newestMessageTime) {
      newestMessageTime = manipulated.sendTime;
      newestMessageId = manipulated.id;
    }
    return accu;
  }, messages);

  newConversation.oldestMessage = {
    id: oldestMessageId,
    sendTime: oldestMessageTime,
  };
  newConversation.newestMessage = {
    id: newestMessageId,
    sendTime: newestMessageTime,
  };

  return newConversation;
}

function checkLastAndFirstMessage(
  conversation: IConversation,
  message: IMessageView
) {
  if (message.sendTime > conversation.newestMessage.sendTime) {
    conversation.newestMessage.sendTime = message.sendTime;
    conversation.newestMessage.id = message.id;
  }

  if (message.sendTime < conversation.oldestMessage.sendTime) {
    conversation.oldestMessage.sendTime = message.sendTime;
    conversation.oldestMessage.id = message.id;
  }
  return conversation;
}

// Reducers
function addConversationsHandler(
  state: IConversationState,
  action: PayloadAction<{
    conversations: Array<IConversation>;
    gotAllConversations?: boolean;
  }>
) {
  if (action.payload.gotAllConversations !== undefined) {
    state.gotAllConversations = action.payload.gotAllConversations;
  }

  for (const c of action.payload.conversations) {
    state.data[c.id] = manipulateConversation(c, state.data[c.id]);
  }
}

function setConversationDataHandler(
  state: IConversationState,
  action: PayloadAction<{ conversation: IConversation }>
) {
  state.data[action.payload.conversation.id] = manipulateConversation(
    action.payload.conversation,
    state.data[action.payload.conversation.id]
  );
}

function loadedOlderMessageHandler(
  state: IConversationState,
  action: PayloadAction<{
    conversationId: number;
    success: boolean;
    gotAllMessages?: boolean;
    error?: any;
  }>
) {
  if (action.payload.success) {
    if (state.data[action.payload.conversationId]?.loadingOlderMessages) {
      delete state.data[action.payload.conversationId].loadingOlderMessages;
    }

    state.data[action.payload.conversationId].loadAllOlderMessages =
      action.payload.gotAllMessages!;
  } else {
    delete state.data[action.payload.conversationId].loadingOlderMessages;

    state.data[action.payload.conversationId].loadOlderMessagesError =
      action.payload.error;
  }
}

function loadingOlderMessageHandler(
  state: IConversationState,
  action: PayloadAction<{
    conversationId: number;
    loadingPromise: Promise<any>;
  }>
) {
  state.data[action.payload.conversationId].loadingOlderMessages =
    action.payload.loadingPromise;
}

function setLastReadTimeHandler(
  state: IConversationState,
  action: PayloadAction<{ conversationId: number; timestamp: number }>
) {
  state.data[action.payload.conversationId].myLastMessageSeen =
    action.payload.timestamp;
}

function conversationAddMessagesHandler(
  state: IConversationState,
  action: PayloadAction<{ messages: Array<IMessageView> }>
) {
  action.payload.messages.forEach((msg) => {
    let currentConversation;
    currentConversation = state.data[msg.conversationId];
    currentConversation = checkLastAndFirstMessage(currentConversation, msg);
    state.data[msg.conversationId] = currentConversation;
  });
}

function conversationAddSingleMessage(
  state: IConversationState,
  action: PayloadAction<{ message: IMessageView; userId?: NumberOrNull }>
) {
  state.data[action.payload.message.conversationId].lastMessageDate =
    action.payload.message.sendTime;
  state.data[action.payload.message.conversationId].messages = [
    action.payload.message,
  ];

  if (action.payload.userId === action.payload.message.senderId) {
    state.data[action.payload.message.conversationId].myLastMessageSeen =
      action.payload.message.sendTime;
  }
}

// Thunks
export const requireConversationFn = async (
  { conversationId }: { conversationId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const conversation = store.conversations.data[conversationId];

  if (conversation) {
    return Promise.resolve();
  }

  return messApi
    .getConversation(store, conversationId, null)
    .then((res) => {
      ThunkApi.dispatch(setConversationData({ conversation: res.data }));
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireConversation = createAsyncThunk(
  'conversation/requireConversation',
  requireConversationFn
);

export const requireEndOfConversationFn = async (
  {
    conversationId,
    conversationToken,
  }: { conversationId: number; conversationToken?: StringOrNull },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const messageId = store.conversations.data[conversationId]?.newestMessage?.id;

  return messApi
    .getConversation(store, conversationId, conversationToken)
    .then(({ data }) => {
      const lastId = (data.messages[0] || {})?.id;
      if (messageId === undefined || !(messageId === lastId)) {
        ThunkApi.dispatch(setConversationData({ conversation: data }));
        ThunkApi.dispatch(
          requireMessagesBeforeMessage({
            conversationId,
            messageId: lastId,
            conversationToken,
            count: 10,
          })
        );
      }

      ThunkApi.dispatch(setConversationData({ conversation: data }));

      return Promise.resolve({ conversation: data });
    });
};
const requireEndOfConversation = createAsyncThunk(
  'conversation/requireEndOfConversation',
  requireEndOfConversationFn
);

export const loadOlderMessagesFn = async (
  {
    conversationId,
    conversationToken = '',
    count = 20,
  }: {
    conversationId: number;
    conversationToken?: string;
    count?: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  if (store.conversations.data[conversationId].loadAllOlderMessages) {
    return Promise.resolve();
  }
  const oldestMessageTime =
    store.conversations.data[conversationId].oldestMessage.sendTime;
  if (oldestMessageTime === Infinity) {
    return Promise.resolve();
  }
  const loadingOlderMessagePromise =
    store.conversations.data[conversationId].loadingOlderMessages;
  if (typeof loadingOlderMessagePromise === 'object') {
    return loadingOlderMessagePromise;
  }
  const loadingPromise = messApi
    .conversation_getMyMessagesBefore(
      store,
      conversationId,
      conversationToken,
      oldestMessageTime,
      count
    )
    .then((res) => {
      let gotAllMessages = false;
      if (res.data.length < count) {
        gotAllMessages = true;
      }

      ThunkApi.dispatch(
        loadedOlderMessage({
          conversationId,
          success: true,
          gotAllMessages,
        })
      );

      ThunkApi.dispatch(addMessages({ messages: res.data }));
    })
    .catch((err) => {
      ThunkApi.dispatch(
        loadedOlderMessage({
          conversationId,
          success: false,
          error: err,
        })
      );
    });
  ThunkApi.dispatch(
    loadingOlderMessage({
      conversationId,
      loadingPromise,
    })
  );
  return loadingPromise;
};
const loadOlderMessages = createAsyncThunk(
  'conversation/loadOlderMessages',
  loadOlderMessagesFn
);

export const requireConversationsByTopicFn = async (
  { userId, topic }: { userId: number; topic: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return messApi.getConversationsByTopic(store, userId, topic).then(
    (res) => {
      ThunkApi.dispatch(
        addConversations({
          conversations: res.data,
        })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const requireConversationsByTopic = createAsyncThunk(
  'conversation/requireConversationsByTopic',
  requireConversationsByTopicFn
);

export const requireConversationByUserIdFn = async (
  { userId }: { userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const thisUserId = Number(store.thisUser.userId);
  const conversations = store.conversations.data;
  const directs = Object.values(conversations).filter(
    (conv) => conv.topic === 'direct'
  );
  const systemId = store.conversations.systemId;
  const hasDirect = directs.filter((direct) => {
    const participantIds = direct.participantIds.filter(
      (id) => id !== thisUserId && id !== systemId && id === userId
    );
    return participantIds.length === 1;
  });

  if (hasDirect.length > 0) {
    return Promise.resolve({ conversationId: hasDirect[0].id });
  }

  return manApi
    .getOrCreateDirectConversation(store, userId)
    .then((res) => {
      const conversationId = res.data;
      return messApi
        .getConversation(store, conversationId)
        .then((response) => {
          ThunkApi.dispatch(
            setConversationData({
              conversation: response.data,
            })
          );
          return Promise.resolve({ conversationId });
        })
        .catch((err) => {
          return ThunkApi.rejectWithValue(err) as any;
        });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const requireConversationByUserId = createAsyncThunk(
  'conversation/requireConversationByUserId',
  requireConversationByUserIdFn
);

const conversationSlice = createSlice({
  name: 'conversation',
  initialState: initialState,
  reducers: {
    addConversations: addConversationsHandler,
    setConversationData: setConversationDataHandler,
    loadedOlderMessage: loadedOlderMessageHandler,
    loadingOlderMessage: loadingOlderMessageHandler,
    setLastReadTime: setLastReadTimeHandler,
    conversationAddMessages: conversationAddMessagesHandler,
    conversationAddSingle: conversationAddSingleMessage,
  },
});

export const {
  addConversations,
  setConversationData,
  loadedOlderMessage,
  loadingOlderMessage,
  setLastReadTime,
  conversationAddMessages,
  conversationAddSingle,
} = conversationSlice.actions;

export {
  requireConversation,
  requireEndOfConversation,
  loadOlderMessages,
  requireConversationsByTopic,
  requireConversationByUserId,
};

export default conversationSlice.reducer;
